import { FormSectionHandler } from '../entity.handler';
import { DashboardResponse, DashboardUpdateRequest, DashboardCreateRequest } from '@key-telematics/fleet-api-client';
import { AdminEditorService } from '../../admin.service';
import { FormBuilderField, FormBuilderKeyValue } from 'app/shared/components/form-builder';
import { TextField, EntityUtils, ToggleField, CostCentreField, ComboField } from '../utils';
import { ICONS } from '../icons';

interface EditorValues {
    name?: string;
    description?: string;
    public?: boolean;
}

export class DashboardDetailsSectionHandler implements FormSectionHandler<DashboardResponse, EditorValues> {

    type = 'form';
    icon = ICONS.dashboard;
    bulk = true;

    title = this.translate('TITLE');
    description = this.translate('DESC');

    constructor(
        public admin: AdminEditorService,
        public ownerId: string
    ) {
    }

    private properties = ['name', 'description', 'public', 'costCentre', 'level'];

    translate(key: string, values?: Object): string {
        return this.admin.translate(`EDITORS.DASHBOARD.DETAILS.${key}`, values);
    }

    applyUpdate(entity: DashboardResponse, payload: DashboardUpdateRequest): Promise<DashboardResponse> {
        return this.admin.applyUpdate(api => api.entities.updateDashboard(entity.id, payload), { _type: 'dashboard' });
    }

    mapEntityToEditor(entity: DashboardResponse): Promise<Partial<DashboardCreateRequest>> {
        const result = {
            name: entity.name,
            description: entity.description,
            public: entity.public,
            costCentre: entity.costCentre ? entity.costCentre : null,
            level: entity.level ? entity.level : null,
        };
        return Promise.resolve(EntityUtils.mergeFields(this.properties, result, {}));
    }

    mapEditorToEntity(entity: DashboardUpdateRequest, values: Partial<DashboardCreateRequest>): Promise<Partial<DashboardCreateRequest>> {
        // cleanup cost center values for non client dashboards
        if (values.level && values.level !== 'client') {
            values.costCentre = null;
        }
        const patch = {
            name: values.name,
            description: values.description,
            public: values.public,
            costCentre: values.costCentre,
            level: values.level,
        };
        return Promise.resolve(EntityUtils.mergeFields(this.properties, patch, entity));
    }

    getFields(entity: DashboardResponse): Promise<FormBuilderField[]> {

        return Promise.resolve().then(() => {
            let costCenterField: FormBuilderField = null;
            let dashboardLevelField: FormBuilderField = null;

            // Build the dashboard create/edit options. Client users should be always add a cost center. Users above client level
            // should first select what company level they are saving the dashboard to. If they select client level
            // then they should choose a cost center. Cost centers and company levels cannot be edited.

            const getLevels = (includeCostCenter?: boolean): FormBuilderKeyValue [] => {
                const companyLevelOptions = ['client', 'vendor', 'distributor', 'system'];
                const companyTranslationKeys = ['LEVEL.CLIENT', 'LEVEL.VENDOR', 'LEVEL.DISTRIBUTOR', 'LEVEL.SYSTEM'];
                const allowedLevels = companyLevelOptions.slice(0, companyLevelOptions.indexOf(this.admin.user.owner.type) + 1);
                return allowedLevels.map((l, idx) => {
                    const fields = (includeCostCenter && l === 'client' ) ? [ CostCentreField(this.ownerId, 'costCentre', this.translate('FIELDS.COST_CENTRE'), true) ] : null; 
                    return { key: l, value: this.translate(companyTranslationKeys[idx]) , fields: fields};
                });
            };

            if (Object.keys(entity || {}).length) {
                if (entity.level === 'client') {
                    costCenterField = {...TextField('costCentre[name]', this.translate('FIELDS.COST_CENTRE'), false, 1, 255), readonly: true };
                    dashboardLevelField = {...ComboField('level', this.translate('FIELDS.LEVEL'), true, getLevels()), readonly: true};
            } else {
                dashboardLevelField = {...ComboField('level', this.translate('FIELDS.LEVEL'), true, getLevels()),readonly: true };
            }
        } else {
            dashboardLevelField = ComboField('level', this.translate('FIELDS.LEVEL'), true, getLevels(true));
        }

            return [
                TextField('name', this.translate('FIELDS.NAME'), true, 1, 255),
                TextField('description', this.translate('FIELDS.DESCRIPTION'), true, 1, 1024),
                ToggleField('public', this.translate('FIELDS.PUBLIC'), true),
                costCenterField,
                dashboardLevelField,
            ].filter(x => x);
        }).then(fields => EntityUtils.prepareBulk(!entity, ['description'], fields));
    }

}
